export type SystemLocale = 'vi' | 'en'
export type SystemDateFormat = 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD'
export type SystemTimeFormat = '24h' | '12h'
export type SmtpEncryption = 'none' | 'tls' | 'ssl'
export type BrandingAssetType = 'logo' | 'favicon'

export interface SaleSettings {
  allow_negative_stock: boolean
  allow_edit_price: boolean
  require_customer: boolean
  allow_line_discount: boolean
  allow_invoice_discount: boolean
  auto_select_single_product: boolean
  max_players_per_table: number
  kitchen_enabled: boolean
  location_management_enabled: boolean
  require_location_on_sale: boolean
  allow_view_cancelled_invoices: boolean
  table_order_enabled: boolean
  sale_grid_enabled: boolean
  sale_return_enabled: boolean
  sale_return_days: number
}

export interface BankAccount {
  id: number
  bank_name: string
  bank_bin?: string | null
  account_number: string
  account_holder: string
  branch_name?: string | null
  is_default: boolean
  is_active: boolean
  note?: string | null
  sort_order: number
  created_at?: string | null
  updated_at?: string | null
}

export interface BankAccountForm {
  bank_name: string
  bank_bin?: string | null
  account_number: string
  account_holder: string
  branch_name?: string | null
  is_default: boolean
  is_active: boolean
  note?: string | null
  sort_order: number
}

export interface SystemSetting {
  display_name: string
  logo_object_key?: string | null
  logo_url?: string | null
  favicon_object_key?: string | null
  favicon_url?: string | null
  primary_color: string
  default_locale: SystemLocale
  date_format: SystemDateFormat
  time_format: SystemTimeFormat
  smtp_host?: string | null
  smtp_port?: number | null
  smtp_encryption?: SmtpEncryption | null
  smtp_username?: string | null
  smtp_password_set?: boolean
  smtp_from_email?: string | null
  smtp_from_name?: string | null
  report_recipient_emails?: string[]
  birthday_email_customer_enabled?: boolean
  birthday_email_staff_enabled?: boolean
  birthday_email_send_hour?: string
  birthday_email_allow_catch_up?: boolean
  birthday_email_customer_subject?: string | null
  birthday_email_customer_body?: string | null
  birthday_email_staff_subject?: string | null
  birthday_email_staff_body?: string | null
  allow_negative_stock: boolean
  allow_edit_price: boolean
  require_customer: boolean
  allow_line_discount: boolean
  allow_invoice_discount: boolean
  auto_select_single_product: boolean
  max_players_per_table: number
  kitchen_enabled: boolean
  location_management_enabled: boolean
  require_location_on_sale: boolean
  allow_view_cancelled_invoices: boolean
  table_order_enabled: boolean
  sale_grid_enabled: boolean
  sale_return_enabled: boolean
  sale_return_days: number
  bank_accounts?: BankAccount[]
  updated_at?: string | null
}

export interface SystemSettingForm {
  updated_at?: string | null
  display_name: string
  primary_color: string
  default_locale: SystemLocale
  date_format: SystemDateFormat
  time_format: SystemTimeFormat
  smtp_host: string
  smtp_port: number | null
  smtp_encryption: SmtpEncryption | null
  smtp_username: string
  smtp_password: string
  smtp_from_email: string
  smtp_from_name: string
  report_recipient_emails: string[]
  birthday_email_customer_enabled: boolean
  birthday_email_staff_enabled: boolean
  birthday_email_send_hour: string
  birthday_email_allow_catch_up: boolean
  birthday_email_customer_subject: string
  birthday_email_customer_body: string
  birthday_email_staff_subject: string
  birthday_email_staff_body: string
  allow_negative_stock: boolean
  allow_edit_price: boolean
  require_customer: boolean
  allow_line_discount: boolean
  allow_invoice_discount: boolean
  auto_select_single_product: boolean
  max_players_per_table: number
  kitchen_enabled: boolean
  location_management_enabled: boolean
  require_location_on_sale: boolean
  allow_view_cancelled_invoices: boolean
  table_order_enabled: boolean
  sale_grid_enabled: boolean
  sale_return_enabled: boolean
  sale_return_days: number
}

export interface SystemSettingPublic {
  display_name: string
  logo_url?: string | null
  favicon_url?: string | null
  primary_color: string
  default_locale: SystemLocale
  date_format: SystemDateFormat
  time_format: SystemTimeFormat
  sale_settings: SaleSettings
}

export interface BrandingPresignPayload {
  type: BrandingAssetType
  filename: string
  mime_type: string
  size: number
}

export interface BrandingPresignResponse {
  upload_url: string
  headers?: Record<string, string>
  object_key: string
  expires_in: number
}

export const DEFAULT_SALE_SETTINGS: SaleSettings = {
  allow_negative_stock: false,
  allow_edit_price: false,
  require_customer: false,
  allow_line_discount: true,
  allow_invoice_discount: true,
  auto_select_single_product: false,
  max_players_per_table: 10,
  kitchen_enabled: true,
  location_management_enabled: false,
  require_location_on_sale: false,
  allow_view_cancelled_invoices: true,
  table_order_enabled: false,
  sale_grid_enabled: false,
  sale_return_enabled: true,
  sale_return_days: 0,
}

export const DEFAULT_BIRTHDAY_EMAIL_SEND_HOUR = '08:00'
export const DEFAULT_BIRTHDAY_EMAIL_CUSTOMER_SUBJECT =
  'Chúc mừng sinh nhật quý khách {name}!'
export const DEFAULT_BIRTHDAY_EMAIL_CUSTOMER_BODY =
  'Kính gửi quý khách {name},\n\nNhân dịp sinh nhật của quý khách ({birth_date}), {store_name} xin gửi tới quý khách lời chúc mừng nồng nhiệt nhất! Chúc quý khách luôn nhiều niềm vui, sức khỏe và thành công trong cuộc sống.\n\nCảm ơn quý khách đã luôn đồng hành cùng {store_name}.\n\nTrân trọng,\n{store_name}'
export const DEFAULT_BIRTHDAY_EMAIL_STAFF_SUBJECT = 'Chúc mừng sinh nhật {name}!'
export const DEFAULT_BIRTHDAY_EMAIL_STAFF_BODY =
  'Thân gửi bạn {name},\n\nNhân ngày sinh nhật của bạn ({birth_date}), ban giám đốc cùng toàn thể đồng nghiệp tại {store_name} xin gửi tới bạn lời chúc mừng ấm áp nhất! Chúc bạn tuổi mới ngập tràn niềm vui, nhiều may mắn và gặt hái thêm nhiều thành tựu mới.\n\nCảm ơn những đóng góp và sự nhiệt huyết của bạn dành cho {store_name}.\n\nThân mến,\n{store_name}'

export const BIRTHDAY_EMAIL_TEMPLATE_VARIABLES = [
  { key: '{name}', label: 'Tên người nhận' },
  { key: '{store_name}', label: 'Tên cửa hàng' },
  { key: '{birth_date}', label: 'Ngày sinh' },
  { key: '{age}', label: 'Tuổi' },
  { key: '{year}', label: 'Năm' },
] as const

export function createEmptySystemSettingForm(): SystemSettingForm {
  return {
    display_name: 'BigSale',
    primary_color: '#2563EB',
    default_locale: 'vi',
    date_format: 'DD/MM/YYYY',
    time_format: '24h',
    smtp_host: '',
    smtp_port: null,
    smtp_encryption: 'tls',
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: '',
    report_recipient_emails: [],
    birthday_email_customer_enabled: false,
    birthday_email_staff_enabled: false,
    birthday_email_send_hour: DEFAULT_BIRTHDAY_EMAIL_SEND_HOUR,
    birthday_email_allow_catch_up: false,
    birthday_email_customer_subject: DEFAULT_BIRTHDAY_EMAIL_CUSTOMER_SUBJECT,
    birthday_email_customer_body: DEFAULT_BIRTHDAY_EMAIL_CUSTOMER_BODY,
    birthday_email_staff_subject: DEFAULT_BIRTHDAY_EMAIL_STAFF_SUBJECT,
    birthday_email_staff_body: DEFAULT_BIRTHDAY_EMAIL_STAFF_BODY,
    allow_negative_stock: false,
    allow_edit_price: false,
    require_customer: false,
    allow_line_discount: true,
    allow_invoice_discount: true,
    auto_select_single_product: false,
    max_players_per_table: 10,
    kitchen_enabled: true,
    location_management_enabled: false,
    require_location_on_sale: false,
    allow_view_cancelled_invoices: true,
    table_order_enabled: false,
    sale_grid_enabled: false,
    sale_return_enabled: true,
    sale_return_days: 0,
  }
}

export function createEmptyBankAccountForm(): BankAccountForm {
  return {
    bank_name: '',
    bank_bin: '',
    account_number: '',
    account_holder: '',
    branch_name: '',
    is_default: false,
    is_active: true,
    note: '',
    sort_order: 0,
  }
}

export function toSystemSettingForm(setting: SystemSetting): SystemSettingForm {
  return {
    updated_at: setting.updated_at ?? null,
    display_name: setting.display_name || 'BigSale',
    primary_color: setting.primary_color || '#2563EB',
    default_locale: setting.default_locale || 'vi',
    date_format: setting.date_format || 'DD/MM/YYYY',
    time_format: setting.time_format || '24h',
    smtp_host: setting.smtp_host || '',
    smtp_port: setting.smtp_port ?? null,
    smtp_encryption: setting.smtp_encryption ?? 'tls',
    smtp_username: setting.smtp_username || '',
    smtp_password: '',
    smtp_from_email: setting.smtp_from_email || '',
    smtp_from_name: setting.smtp_from_name || '',
    report_recipient_emails: setting.report_recipient_emails || [],
    birthday_email_customer_enabled: Boolean(setting.birthday_email_customer_enabled),
    birthday_email_staff_enabled: Boolean(setting.birthday_email_staff_enabled),
    birthday_email_send_hour: setting.birthday_email_send_hour || DEFAULT_BIRTHDAY_EMAIL_SEND_HOUR,
    birthday_email_allow_catch_up: Boolean(setting.birthday_email_allow_catch_up),
    birthday_email_customer_subject:
      setting.birthday_email_customer_subject || DEFAULT_BIRTHDAY_EMAIL_CUSTOMER_SUBJECT,
    birthday_email_customer_body:
      setting.birthday_email_customer_body || DEFAULT_BIRTHDAY_EMAIL_CUSTOMER_BODY,
    birthday_email_staff_subject:
      setting.birthday_email_staff_subject || DEFAULT_BIRTHDAY_EMAIL_STAFF_SUBJECT,
    birthday_email_staff_body:
      setting.birthday_email_staff_body || DEFAULT_BIRTHDAY_EMAIL_STAFF_BODY,
    allow_negative_stock: Boolean(setting.allow_negative_stock),
    allow_edit_price: Boolean(setting.allow_edit_price),
    require_customer: Boolean(setting.require_customer),
    allow_line_discount: setting.allow_line_discount !== false,
    allow_invoice_discount: setting.allow_invoice_discount !== false,
    auto_select_single_product: Boolean(setting.auto_select_single_product),
    max_players_per_table: setting.max_players_per_table || 10,
    kitchen_enabled: setting.kitchen_enabled !== false,
    location_management_enabled: Boolean(setting.location_management_enabled),
    require_location_on_sale: Boolean(setting.require_location_on_sale),
    allow_view_cancelled_invoices: setting.allow_view_cancelled_invoices !== false,
    table_order_enabled: Boolean(setting.table_order_enabled),
    sale_grid_enabled: Boolean(setting.sale_grid_enabled),
    sale_return_enabled: true,
    sale_return_days: Number(setting.sale_return_days ?? 0),
  }
}
